/**
 * Success Screen - Tela 8
 *
 * Displays payment success confirmation with order details.
 */

import type { OrderInfo } from '@/types/voice-dtmf.types';
import { maskToLastFour } from '@utils/cardUtils';
import styles from './Success.module.css';

interface SuccessProps {
  orderInfo: OrderInfo;
  showPaymentMethod?: boolean;
}

export const Success = ({
  orderInfo,
  showPaymentMethod = true,
}: SuccessProps) => {
  const formatAmount = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
    }).format(amount / 100);
  };

  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className={styles.container}>
      {/* Success Icon */}
      <div className={styles.iconContainer}>
        <svg
          className={styles.successIcon}
          width="121"
          height="85"
          viewBox="0 0 121 85"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            opacity="0.1"
            d="M118.638 20.0208C121.762 16.8966 121.762 11.8313 118.638 8.7071L112.274 2.34316C109.15 -0.781046 104.084 -0.781055 100.96 2.34314L54.9981 48.305C53.436 49.8671 50.9034 49.8671 49.3413 48.305L32.3708 31.3345C29.2466 28.2103 24.1812 28.2103 21.057 31.3345L14.6931 37.6985C11.5689 40.8226 11.5689 45.8879 14.6931 49.0121L46.5127 80.8319C49.6369 83.9562 54.7023 83.9562 57.8265 80.832L118.638 20.0208Z"
            fill="#06A4F3"
          />
          <path
            d="M89.3174 4.22131C92.051 1.48804 96.4832 1.4879 99.2168 4.22131L105.58 10.5856C108.314 13.3192 108.314 17.7513 105.58 20.485L44.7695 81.2965C42.0359 84.0301 37.6038 84.03 34.8701 81.2965L3.0498 49.4762C0.316567 46.7425 0.316355 42.3103 3.0498 39.5768L9.41406 33.2135C12.1477 30.4798 16.5798 30.4798 19.3135 33.2135L36.2842 50.1832C38.2368 52.1358 41.4029 52.1358 43.3555 50.1832L89.3174 4.22131Z"
            stroke="#06A4F3"
            strokeWidth="2"
          />
        </svg>
      </div>

      <div className={styles.section}>
        {/* Title */}
        <h2 className={styles.title}>
          Your payment has been successfully processed!
        </h2>

        {/* Subtitle */}
        <p className={styles.subtitle}>You can check payment details below</p>
      </div>
      <div className={styles.paymentInfoSection}>
        {/* Amount Paid Section */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Amount paid</div>
          <div className={styles.amountValue}>
            {formatAmount(orderInfo.amount, orderInfo.currency)}
          </div>
        </div>

        {/* Payment Information Section */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Payment information</div>
          <div className={styles.infoList}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Order ID:</span>{' '}
              <span className={styles.infoValue}>
                {orderInfo.merchantTransactionId ?? '-'}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>BT Pay ID:</span>{' '}
              <span className={styles.infoValue}>{orderInfo.btPayId}</span>
            </div>
          </div>
        </div>

        {/* Payment Method Section */}
        {showPaymentMethod && (
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Payment Method</div>
            <div className={styles.infoList}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Card number:</span>{' '}
                <span className={styles.infoValue}>
                  {maskToLastFour(orderInfo.maskedCardNumber)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Card type:</span>{' '}
                <span className={styles.infoValue}>{orderInfo.cardType}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Expiration date:</span>{' '}
                <span className={styles.infoValue}>{orderInfo.expiryDate}</span>
              </div>
            </div>
          </div>
        )}

        {/* Timestamp */}
        <div className={styles.timestamp}>
          {formatTimestamp(orderInfo.timestamp)}
        </div>
      </div>
    </div>
  );
};
